import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Container, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { ArrowLeft, Eye } from "react-feather";
import Breadcrumb from "../common/breadcrumb";
import ToggleSwitch from "../../shared/ToggleSwitch";
import { C, reportingGlobalCSS } from "../reporting/reportingStyles";
import { RCard, Badge, Loader } from "../reporting/ReportingShared";
import { isMerchantScoped, isReadOnly } from "../../constants/roles";
import { successToast } from "../../shared/Toast";
import { createPage, updatePage, getPage, listPages, setPageStatus } from "../../utils/merchantPagesApi";
import {
  LIMITS,
  PAGES_BASE_URL,
  PLACEHOLDERS,
  SLUG_SUGGESTIONS,
  STATUS_META,
  emptyPage,
  formFromPage,
  slugError,
  slugify,
} from "../../constants/merchantPages";
import BilingualField from "./BilingualField";
import SectionsEditor from "./SectionsEditor";
import PagePreviewModal from "./PagePreviewModal";
import TemplatePickerModal from "./TemplatePickerModal";
import { inputStyle, labelStyle, hintStyle, errorText, buttonStyle, btnSecondary, chip, notice } from "./pageStyles";

const clone = (v) => JSON.parse(JSON.stringify(v));
const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);

const Row = ({ label, hint, children }) => (
  <div style={{ marginBottom: 14 }}>
    <span style={labelStyle}>{label}</span>
    {children}
    {hint && <div style={hintStyle}>{hint}</div>}
  </div>
);

/**
 * Pages → New page / Edit page.
 *
 * Saving a published page puts the change live immediately (there is no second "draft of a
 * published page"); the button says so. Publishing an unsaved draft saves it first.
 *
 * The router is a plain BrowserRouter, so react-router's useBlocker is unavailable; leaving with
 * unsaved changes is guarded by the Back button's confirm and a beforeunload prompt.
 */
const PageEditor = () => {
  const { id } = useParams();
  const isNew = !id || id === "new";
  const navigate = useNavigate();

  const userType = localStorage.getItem("userType");
  const { selectedMerchant: reduxSelectedMerchant } = useSelector((state) => state.merchant || {});
  const scopedMerchantId = isMerchantScoped(userType) ? localStorage.getItem("MerchantId") : reduxSelectedMerchant;
  const readOnly = isReadOnly(userType);

  const [loading, setLoading] = useState(!isNew);
  const [record, setRecord] = useState(null); // the saved row: id, status, publicUrl…
  const [merchant, setMerchant] = useState(null); // { id, name, slug, publicBaseUrl }
  const [form, setForm] = useState(emptyPage);
  const [baseline, setBaseline] = useState(emptyPage);
  const [slugTouched, setSlugTouched] = useState(false);
  const [errors, setErrors] = useState([]);
  const [saving, setSaving] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(isNew && !readOnly);
  const [preview, setPreview] = useState(null); // { page, lang }
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [unpublishOpen, setUnpublishOpen] = useState(false);
  const [placeholdersOpen, setPlaceholdersOpen] = useState(false);

  const merchantId = record?.merchantId ?? (scopedMerchantId && scopedMerchantId !== "all" ? scopedMerchantId : null);
  const isPublished = record?.status === "published";
  const dirty = !same(form, baseline);
  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  // ── load ─────────────────────────────────────────────────────────────
  useEffect(() => {
    let live = true;
    if (isNew) {
      if (!merchantId) return undefined;
      listPages({ merchantId, limit: 1 }).then((d) => { if (live) setMerchant(d.merchant); });
      return () => { live = false; };
    }
    setLoading(true);
    getPage(id).then((p) => {
      if (!live) return;
      if (p) {
        setRecord(p);
        setMerchant(p.merchant);
        const f = formFromPage(p);
        setForm(f);
        setBaseline(f);
        setSlugTouched(true);
      }
      setLoading(false);
    });
    return () => { live = false; };
    // merchantId only matters for a new page; an existing page carries its own.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isNew]);

  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  // ── form helpers ─────────────────────────────────────────────────────
  const onTitleEn = (pair) => {
    setForm((f) => ({
      ...f,
      titleEn: pair.en || "",
      titleAr: pair.ar || "",
      slug: isNew && !slugTouched ? slugify(pair.en || "") : f.slug,
    }));
  };

  const pickTemplate = (t) => {
    setPickerOpen(false);
    if (!t) { setSlugTouched(false); return; }
    setForm({
      ...emptyPage(),
      slug: t.defaultSlug,
      titleEn: t.title?.en || "",
      titleAr: t.title?.ar || "",
      sections: clone(t.sections),
      showToc: t.showToc !== false,
    });
    setSlugTouched(true);
  };

  const slugProblem = slugError(form.slug);
  const titleProblem = !String(form.titleEn || "").trim() ? "English title is required." : null;
  const publicPath = `${merchant?.publicBaseUrl || PAGES_BASE_URL}/${merchant?.slug || "<merchant-slug>"}/${form.slug || "<page-slug>"}`;

  // ── save / publish ───────────────────────────────────────────────────
  const save = useCallback(async () => {
    if (slugProblem || titleProblem) {
      setErrors([titleProblem, slugProblem].filter(Boolean));
      return null;
    }
    setSaving(true);
    setErrors([]);
    const body = { ...form };
    const result = record
      ? await updatePage(record.id, body)
      : await createPage({ ...body, merchantId: merchantId ? Number(merchantId) : undefined });
    setSaving(false);

    if (!result.data) {
      setErrors(result.errors?.length ? result.errors : [result.error]);
      return null;
    }
    const saved = result.data;
    const f = formFromPage(saved);
    setRecord(saved);
    setForm(f);
    setBaseline(f);
    if (!record) {
      successToast("Draft saved");
      navigate(`/pages/${saved.id}`, { replace: true });
    } else {
      successToast(saved.status === "published" ? "Saved — the change is live" : "Draft saved");
    }
    return saved;
  }, [form, record, merchantId, navigate, slugProblem, titleProblem]);

  const publish = async () => {
    let target = record;
    if (!target || dirty) {
      target = await save();
      if (!target) return;
    }
    setSaving(true);
    const updated = await setPageStatus(target.id, "published");
    setSaving(false);
    if (updated) {
      setRecord(updated);
      successToast("Published — the page is live");
    }
  };

  const unpublish = async () => {
    setSaving(true);
    const updated = await setPageStatus(record.id, "draft");
    setSaving(false);
    setUnpublishOpen(false);
    if (updated) {
      setRecord(updated);
      successToast("Unpublished — the page is offline");
    }
  };

  const goBack = () => (dirty && !readOnly ? setLeaveOpen(true) : navigate("/pages"));

  const status = STATUS_META[record?.status || "draft"];
  const canPublish = Boolean(merchant?.slug);
  const blocks = form.sections?.blocks || [];
  const previewPayload = useMemo(() => preview?.page, [preview]);

  if (!isNew && loading) return <Loader />;
  if (!isNew && !record) {
    return (
      <Container fluid><RCard><div style={{ padding: 40, textAlign: "center", color: C.textMed }}>Page not found.</div></RCard></Container>
    );
  }
  if (isNew && !merchantId) {
    return (
      <Fragment>
        <Breadcrumb title="New page" parent="Pages" />
        <Container fluid><RCard><div style={{ padding: 40, textAlign: "center", color: C.textMed }}>Select a merchant in the header first.</div></RCard></Container>
      </Fragment>
    );
  }

  return (
    <Fragment>
      <Breadcrumb title={isNew ? "New page" : form.titleEn || "Edit page"} parent="Pages" />
      <Container fluid>
        <style>{reportingGlobalCSS}</style>
        <div className="rpt-wrap">
          {/* ── top bar ── */}
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
            <button type="button" style={btnSecondary} onClick={goBack}><ArrowLeft size={14} /> Pages</button>
            <Badge color={status.color} bg={status.bg}>{status.label}</Badge>
            {dirty && !readOnly && <span style={{ fontSize: 12, color: C.orange, fontWeight: 600 }}>Unsaved changes</span>}
            <span style={{ flex: 1 }} />
            <button type="button" style={btnSecondary} onClick={() => setPreview({ page: clone(form), lang: "en" })}><Eye size={14} /> Preview</button>
            <button type="button" style={btnSecondary} onClick={() => setPreview({ page: clone(form), lang: "ar" })}>معاينة</button>
            {!readOnly && (
              <>
                <button type="button" style={isPublished ? buttonStyle : btnSecondary} disabled={saving || (!dirty && !isNew)} onClick={save}>
                  {saving ? "Saving…" : isPublished ? "Save — goes live" : "Save draft"}
                </button>
                {isPublished ? (
                  <button type="button" style={btnSecondary} disabled={saving} onClick={() => setUnpublishOpen(true)}>Unpublish</button>
                ) : (
                  <button
                    type="button"
                    style={{ ...buttonStyle, opacity: canPublish ? 1 : 0.5 }}
                    disabled={saving || !canPublish}
                    title={canPublish ? "" : "This merchant needs a URL slug before pages can be published"}
                    onClick={publish}
                  >
                    {dirty || !record ? "Save & publish" : "Publish"}
                  </button>
                )}
              </>
            )}
          </div>

          {merchant && !merchant.slug && (
            <div style={notice("warning")}>
              {userType === "admin" ? (
                <>This merchant has no URL slug yet, so pages can be drafted but not published. Set one in{" "}
                  <a href={`/merchant/edit-merchant/${merchant.id}`} style={{ color: "inherit" }}>Edit Merchant → Merchant Details</a>.</>
              ) : (
                <>This merchant has no URL slug yet, so pages can be drafted but not published. Ask your Wags admin to set one.</>
              )}
            </div>
          )}
          {readOnly && <div style={notice("info")}>You can view and preview pages, but not change them.</div>}

          {errors.length > 0 && (
            <div style={notice("error")}>
              {errors.length === 1 ? errors[0] : (
                <>Fix these before saving:<ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>{errors.map((e) => <li key={e}>{e}</li>)}</ul></>
              )}
            </div>
          )}

          <div style={{ display: "grid", gridTemplateColumns: "minmax(280px, 340px) 1fr", gap: 20, alignItems: "start" }}>
            {/* ── settings ── */}
            <div style={{ position: "sticky", top: 16 }}>
              <RCard>
                <strong style={{ display: "block", fontSize: 14, color: C.text, marginBottom: 14 }}>Page settings</strong>

                <BilingualField
                  label="Title"
                  value={{ en: form.titleEn || null, ar: form.titleAr || null }}
                  onChange={onTitleEn}
                  maxLength={LIMITS.title}
                  disabled={readOnly}
                  requiredEn
                  placeholder="e.g. Privacy Policy"
                  testId="page-title"
                />

                <Row label="URL slug">
                  <input
                    aria-label="URL slug"
                    style={{ ...inputStyle, fontFamily: "monospace" }}
                    value={form.slug}
                    disabled={readOnly}
                    maxLength={LIMITS.slug}
                    onChange={(e) => { setSlugTouched(true); set("slug", e.target.value.toLowerCase()); }}
                  />
                  {form.slug && slugProblem && <div style={errorText}>{slugProblem}</div>}
                  {!readOnly && (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6 }}>
                      {SLUG_SUGGESTIONS.map((s) => (
                        <button key={s.slug} type="button" style={chip(form.slug === s.slug)} onClick={() => { setSlugTouched(true); set("slug", s.slug); }}>
                          {s.slug}
                        </button>
                      ))}
                    </div>
                  )}
                  <div style={{ ...hintStyle, wordBreak: "break-all", fontFamily: "monospace" }}>{publicPath}</div>
                  {isPublished && record && form.slug !== record.slug && (
                    <div style={{ ...errorText, color: C.orange }}>Changing the slug of a live page breaks links already shared, such as an App Store listing.</div>
                  )}
                </Row>

                <Row label="Show table of contents" hint="Listed at the top when the page has two or more headings.">
                  <ToggleSwitch checked={Boolean(form.showToc)} handleToggle={(v) => set("showToc", v)} disabled={readOnly} />
                </Row>

                <Row label="Last updated on" hint="Shown under the title and as {{lastUpdated}}. Leave blank to use the day you first publish.">
                  <input
                    type="date"
                    aria-label="Last updated on"
                    style={inputStyle}
                    value={form.lastUpdatedOn || ""}
                    disabled={readOnly}
                    onChange={(e) => set("lastUpdatedOn", e.target.value)}
                  />
                </Row>

                <BilingualField
                  label="Search description (optional)"
                  kind="textarea"
                  value={{ en: form.metaDescriptionEn || null, ar: form.metaDescriptionAr || null }}
                  onChange={(p) => setForm((f) => ({ ...f, metaDescriptionEn: p.en || "", metaDescriptionAr: p.ar || "" }))}
                  maxLength={LIMITS.metaDescription}
                  disabled={readOnly}
                  hint="The summary search engines show under the link."
                />

                <Row label="Hide from search engines" hint="Off for policies: app stores and customers should be able to find them.">
                  <ToggleSwitch checked={Boolean(form.noindex)} handleToggle={(v) => set("noindex", v)} disabled={readOnly} />
                </Row>

                <button type="button" style={{ ...chip(placeholdersOpen), marginTop: 4 }} onClick={() => setPlaceholdersOpen((o) => !o)}>
                  {placeholdersOpen ? "Hide placeholders" : "Placeholders you can use"}
                </button>
                {placeholdersOpen && (
                  <div style={{ marginTop: 8 }}>
                    {PLACEHOLDERS.map((p) => (
                      <div key={p.token} style={{ fontSize: 12, marginBottom: 4 }}>
                        <code style={{ color: C.coralDark }}>{p.token}</code>
                        <span style={{ color: C.textMed }}> — {p.hint}</span>
                      </div>
                    ))}
                    <div style={hintStyle}>Type them into any text. They are filled in for each merchant when the page is shown.</div>
                  </div>
                )}
              </RCard>
            </div>

            {/* ── sections ── */}
            <div>
              <SectionsEditor
                blocks={blocks}
                onChange={(next) => set("sections", { version: 1, blocks: next })}
                disabled={readOnly}
              />
            </div>
          </div>
        </div>
      </Container>

      <TemplatePickerModal
        isOpen={pickerOpen}
        onPick={pickTemplate}
        onClose={() => setPickerOpen(false)}
      />

      <PagePreviewModal
        isOpen={Boolean(preview)}
        onClose={() => setPreview(null)}
        merchantId={merchantId}
        page={previewPayload}
        initialLang={preview?.lang || "en"}
      />

      <Modal isOpen={leaveOpen} toggle={() => setLeaveOpen(false)}>
        <ModalHeader toggle={() => setLeaveOpen(false)}>Leave without saving?</ModalHeader>
        <ModalBody style={{ color: C.textMed }}>Your changes to this page have not been saved and will be lost.</ModalBody>
        <ModalFooter style={{ borderTop: `1px solid ${C.border}` }}>
          <button type="button" style={btnSecondary} onClick={() => setLeaveOpen(false)}>Stay</button>
          <button type="button" style={buttonStyle} onClick={() => navigate("/pages")}>Leave without saving</button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={unpublishOpen} toggle={() => !saving && setUnpublishOpen(false)}>
        <ModalHeader toggle={() => !saving && setUnpublishOpen(false)}>Unpublish this page?</ModalHeader>
        <ModalBody style={{ color: C.textMed, lineHeight: 1.6 }}>
          The public link stops working (browsers may keep a copy for up to 5 minutes). If this is the policy linked from an
          App Store or Google Play listing, the listing will point at a missing page.
        </ModalBody>
        <ModalFooter style={{ borderTop: `1px solid ${C.border}` }}>
          <button type="button" style={btnSecondary} disabled={saving} onClick={() => setUnpublishOpen(false)}>Back</button>
          <button type="button" style={buttonStyle} disabled={saving} onClick={unpublish}>{saving ? "Working…" : "Yes, unpublish"}</button>
        </ModalFooter>
      </Modal>
    </Fragment>
  );
};

export default PageEditor;
